import React, { PropsWithChildren } from 'react'
import { ContactCtaFragment, FourColCtaFragment, GlobalLinksCtaFragment, Icon, LinksCtaFragment } from '@gql'
import { FourColCta } from './cta'
import {IconCodeType} from "../components/icon/CreditUnionIcon";

export type ContactCtaProps = {
  data: ContactCtaFragment[] | LinksCtaFragment[]
  fallback?: GlobalLinksCtaFragment
}



const contactCtaFragmentToFourColCtaFragment = (data: ContactCtaFragment): FourColCtaFragment => ({
  ctaTitle: data.contactCtaSubtitle,
  ctaSubtitle: data.contactCtaTitle,
  topLeft: [{
    __typename: 'topLeft_BlockType',
    icon: {
      icon: data.contactCTAIcon1,
      variant: 'line',
    },
    subtext: data.contactCTASubtitle1,
    button: {
      text: data.contactCTATitle1,
      url: data.contactCTALink1,
      target: '_self',
    },
  }],
  topRight: [{
    __typename: 'topRight_BlockType',
    icon: {
      icon: data.contactCTAIcon2,
      variant: 'line',
    },
    subtext: data.contactCTASubtitle2,
    button: {
      text: data.contactCTATitle2,
      url: data.contactCTALink2,
      target: '_self',
    },
  }],
  bottomLeft: [{
    __typename: 'bottomLeft_BlockType',
    icon: {
      icon: data.contactCTAIcon3,
      variant: 'line',
    },
    subtext: data.contactCTASubtitle3,
    button: {
      text: data.contactCTATitle3,
      url: data.contactCTALink3,
      target: '_self',
    },
  }],
  bottomRight: [{
    __typename: 'bottomRight_BlockType',
    icon: {
      icon: data.contactCTAIcon4,
      variant: 'line',
    },
    subtext: data.contactCTASubtitle4,
    button: {
      text: data.contactCTATitle4,
      url: data.contactCTALink4,
      target: '_self',
    },
  }],
})

const linksCtaFragmentToFourColCtaFragment = (data: LinksCtaFragment): FourColCtaFragment => ({
  ctaTitle: data.linksCtaSubtitle,
  ctaSubtitle: data.linksCtaTitle,
  topLeft: [{
    __typename: 'topLeft_BlockType',
    icon: {
      icon: data.linksCta1Icon,
      variant: 'line',
    },
    subtext: data.linksCta1Subtext,
    button: {
      text: data.linksCTALink1?.customText,
      url: data.linksCTALink1?.url,
      target: data.linksCTALink1?.target || '_self',
    },
  }],
  topRight: [{
    __typename: 'topRight_BlockType',
    icon: {
      icon: data.linksCta2Icon,
      variant: 'line',
    },
    subtext: data.linksCta2Subtext,
    button: {
      text: data.linksCTALink2?.customText,
      url: data.linksCTALink2?.url,
      target: data.linksCTALink2?.target || '_self',
    },
  }],
  bottomLeft: [{
    __typename: 'bottomLeft_BlockType',
    icon: {
      icon: data.linksCta3Icon,
      variant: 'line',
    },
    subtext: data.linksCta3Subtext,
    button: {
      text: data.linksCTALink3?.customText,
      url: data.linksCTALink3?.url,
      target: data.linksCTALink3?.target || '_self',
    },
  }],
  bottomRight: [{
    __typename: 'bottomRight_BlockType',
    icon: {
      icon: data.linksCta4Icon,
      variant: 'line',
    },
    subtext: data.linksCta4Subtext,
    button: {
      text: data.linksCTALink4?.customText,
      url: data.linksCTALink4?.url,
      target: data.linksCTALink4?.target || '_self',
    },
  }],
})

const fallbackCtaFragmentToFourColCtaFragment = (linksCta: NonNullable<GlobalLinksCtaFragment['globalLinksCta'][number]>): FourColCtaFragment => {
  return {
    ctaTitle: linksCta.linksCtaTitle,
    ctaSubtitle: linksCta.linksCtaSubtitle,
    topLeft: [{
      __typename: 'topLeft_BlockType',
      icon: {
        icon: linksCta.linksCta1Icon,
        variant: 'line',
      },
      subtext: linksCta.linksCta1Subtext,
      button: {
        text: linksCta.linksCTALink1?.customText,
        url: linksCta.linksCTALink1?.url,
        target: linksCta.linksCTALink1?.target || '_self',
      },
    }],
    topRight: [{
      __typename: 'topRight_BlockType',
      icon: {
        icon: linksCta.linksCta2Icon,
        variant: 'line',
      },
      subtext: linksCta.linksCta2Subtext,
      button: {
        text: linksCta.linksCTALink2?.customText,
        url: linksCta.linksCTALink2?.url,
        target: linksCta.linksCTALink2?.target || '_self',
      },
    }],
    bottomLeft: [{
      __typename: 'bottomLeft_BlockType',
      icon: {
        icon: linksCta.linksCta3Icon,
        variant: 'line',
      },
      subtext: linksCta.linksCta3Subtext,
      button: {
        text: linksCta.linksCTALink3?.customText,
        url: linksCta.linksCTALink3?.url,
        target: linksCta.linksCTALink3?.target || '_self',
      },
    }],
    bottomRight: [{
      __typename: 'bottomRight_BlockType',
      icon: {
        icon: linksCta.linksCta4Icon,
        variant: 'line',
      },
      subtext: linksCta.linksCta4Subtext,
      button: {
        text: linksCta.linksCTALink4?.customText,
        url: linksCta.linksCTALink4?.url,
        target: linksCta.linksCTALink4?.target || '_self',
      },
    }],
  }
}

export const ContactCta: React.FC<PropsWithChildren<ContactCtaProps>> = ({ data, fallback }) => {

  if (data == undefined || data[0] == undefined ){
    return null
  }

  const contact = data[0]
  const fallbackCTA = fallback?.globalLinksCta?.[0]
  if (contact) {
    if (contact.__typename === 'contactCta_BlockType') {
      return <FourColCta { ...contactCtaFragmentToFourColCtaFragment(contact) } />
    } else if (contact.__typename === 'linksCta_BlockType') {
      return <FourColCta { ...linksCtaFragmentToFourColCtaFragment(contact) } />
    }
  }

  if (fallbackCTA) {
    return <FourColCta { ...fallbackCtaFragmentToFourColCtaFragment(fallbackCTA) } />
  }

  return null
}
